/* eslint-disable unicorn/filename-case -- TODO rename later */
import { AuthenticationStore } from "@istari-frontend/auth";
import { IstariClient } from "@istari/istari-client";

import { resolveRegistryTarget } from "~/shared/config/registry-target";

import { refreshAccessToken } from "../external-api/auth";

const getClientSDK = () => {
  const AS = AuthenticationStore();
  const registryAuthToken = AS.getAccessToken() || "";
  const target = resolveRegistryTarget();
  return target.kind === "gateway"
    ? new IstariClient({ digitalApiUrl: target.digitalApiUrl, registryAuthToken })
    : new IstariClient(target.registryUrl, registryAuthToken);
};

const isAccessTokenExpired = () => {
  const AS = AuthenticationStore();
  const tokenExpTimestamp = AS.getAccessTokenExpires();

  if (!tokenExpTimestamp) {
    return false;
  }

  return Date.now() > tokenExpTimestamp * 1000;
};

const isResponseErrorWithStatus = (error: unknown, expectedStatus: number): error is { response: Response } => {
  if (
    typeof error !== "object" ||
    error === null ||
    !("response" in error) ||
    typeof (error as { response?: unknown }).response !== "object"
  ) {
    return false;
  }

  const response = (error as { response: Response }).response;
  return typeof response.status === "number" && response.status === expectedStatus;
};

const isInvalidTokenError = async (error: unknown) => {
  if (!isResponseErrorWithStatus(error, 400)) {
    return false;
  }

  try {
    const responseBody = await error.response.clone().text();
    return responseBody.includes("Invalid Token");
  } catch {
    return false;
  }
};

const clearSessionAndReload = () => {
  const AS = AuthenticationStore();
  AS.clearSessionToken();
  window.location.reload();
};

const clientSDK = new Proxy(getClientSDK(), {
  get(target, prop, receiver) {
    const overriddenValue = Reflect.get(target, prop, receiver);
    if (Object.hasOwn(target, prop) && typeof overriddenValue === "function") {
      return overriddenValue.bind(target);
    }

    const client = getClientSDK();
    const value = client[prop as keyof IstariClient];

    if (typeof value === "function") {
      return async (...args: unknown[]) => {
        const executeWithCurrentToken = async () => {
          const freshClient = getClientSDK();
          const method = freshClient[prop as keyof IstariClient];
          return await (method as (...methodArgs: unknown[]) => unknown).apply(freshClient, args);
        };

        if (isAccessTokenExpired()) {
          await refreshAccessToken();
        }

        try {
          return await executeWithCurrentToken();
        } catch (error) {
          if (await isInvalidTokenError(error)) {
            await refreshAccessToken();

            try {
              return await executeWithCurrentToken();
            } catch (retryError) {
              if ((await isInvalidTokenError(retryError)) || isResponseErrorWithStatus(retryError, 401)) {
                clearSessionAndReload();
              }
              throw retryError;
            }
          }

          if (isResponseErrorWithStatus(error, 401)) {
            clearSessionAndReload();
          }

          throw error;
        }
      };
    }

    return value;
  },
});

export default clientSDK;
